import type { JsonSchemaType } from '@gmn-portal/backend';
import { useEffect, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useIntl } from '@/core/i18n/use-intl';
import { useScrollSpy } from '@/features/shared/objects/components/use-scroll-spy';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';
import { scrollToTarget } from '@/shared/utils';
import type { ContractFormData } from '../types/contract-form';
import { ContractDetailsSidemenu } from './contract-details-sidemenu';
import {
  buildContractMetadataSections,
  ContractMasterDataSection,
  ContractMetadataSections,
} from './contract-general-sections';

type ContractGeneralTabProps = {
  mode: 'read' | 'edit';
  form: UseFormReturn<ContractFormData, any, ContractFormData>;
  canEditContracts: boolean;
  canReadObjects: boolean;
  isGmnAdmin: boolean;
  groupKey?: string | null;
  disableCustomerSelection?: boolean;
  customerName?: string;
  customerHref?: string | null;
  aggregatedObjectIds: string[];
  aggregatedObjects?: Array<{ id: string; name: string; href?: string | null }>;
  isLoadingObjects?: boolean;
  metadataConfig?: JsonSchemaType;
  metadataSchemaZod: any;
  dynamicSchema?: JsonSchemaType;
  dynamicSchemaZod: any;
  onEditDynamicSchema?: () => void;
};

export function ContractGeneralTab({
  mode,
  form,
  canEditContracts,
  canReadObjects,
  isGmnAdmin,
  groupKey,
  disableCustomerSelection,
  customerName,
  customerHref,
  aggregatedObjectIds,
  aggregatedObjects,
  isLoadingObjects,
  metadataConfig,
  metadataSchemaZod,
  dynamicSchema,
  dynamicSchemaZod,
  onEditDynamicSchema,
}: ContractGeneralTabProps) {
  const { t, locale } = useIntl();
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);

  const predefinedMetadataSections = useMemo(() => {
    return buildContractMetadataSections({
      schema: metadataConfig,
      locale,
      ungroupedLabel: t('metadata.builder.ungrouped'),
      groupIdPrefix: 'contract-metadata-predefined-group',
      ungroupedId: 'contract-metadata-predefined-ungrouped',
      groupKey,
    });
  }, [groupKey, locale, metadataConfig, t]);

  const customMetadataSections = useMemo(() => {
    return buildContractMetadataSections({
      schema: dynamicSchema,
      locale,
      ungroupedLabel: t('metadata.builder.ungrouped'),
      groupIdPrefix: 'contract-metadata-custom-group',
      ungroupedId: 'contract-metadata-custom-ungrouped',
      groupKey,
    });
  }, [dynamicSchema, groupKey, locale, t]);

  const sidemenuItems = useMemo(() => {
    const masterDataItems = [
      {
        id: 'contract-master-data',
        label: t('contracts.sections.masterData'),
        kind: 'master-data' as const,
      },
    ];

    const predefinedItems = predefinedMetadataSections.map((section) => ({
      id: section.id,
      label: section.label,
      kind: 'metadata-group' as const,
    }));

    const customItems = customMetadataSections.map((section) => ({
      id: section.id,
      label: section.label,
      kind: 'custom-group' as const,
    }));

    return [...masterDataItems, ...predefinedItems, ...customItems];
  }, [customMetadataSections, predefinedMetadataSections, t]);

  const activeSectionId = useScrollSpy(
    sidemenuItems.map((i) => i.id),
    { root: scrollRoot, rootMargin: '-15% 0px -75% 0px' }
  );

  const scrollToSection = (id: string) => {
    scrollToTarget(id, { root: scrollRoot });
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setScrollRoot(document.querySelector('main.overflow-y-auto') as HTMLElement | null);
  }, []);

  const predefinedValues = (form.watch('metadata') || {}) as Record<string, unknown>;
  const customValues = (form.watch('dynamicMetadataValues') || {}) as Record<string, unknown>;

  const hasCustomMetadata = Boolean(
    dynamicSchema && Object.keys(dynamicSchema.properties || {}).length > 0
  );

  return (
    <div className="grid gap-6">
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-3">
          <div className="lg:hidden">
            <Select
              value={activeSectionId ?? sidemenuItems[0]?.id ?? 'contract-master-data'}
              onValueChange={(id) => scrollToSection(id)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sidemenuItems.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="hidden lg:block sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-auto">
            <ContractDetailsSidemenu
              items={sidemenuItems}
              activeId={activeSectionId}
              onSelect={scrollToSection}
              showCustomSeparator={hasCustomMetadata}
            />
          </div>
        </div>

        <div className="lg:col-span-9 grid gap-10">
          <ContractMasterDataSection
            id="contract-master-data"
            mode={mode}
            form={form}
            canEditContracts={canEditContracts}
            canReadObjects={canReadObjects}
            disableCustomerSelection={disableCustomerSelection}
            customerName={customerName}
            customerHref={customerHref}
            aggregatedObjectIds={aggregatedObjectIds}
            aggregatedObjects={aggregatedObjects}
            isLoadingObjects={isLoadingObjects}
          />

          <ContractMetadataSections
            mode={mode}
            form={form}
            predefinedSchema={metadataConfig}
            predefinedZod={metadataSchemaZod}
            predefinedValues={predefinedValues}
            predefinedSections={predefinedMetadataSections}
            dynamicSchema={dynamicSchema}
            dynamicZod={dynamicSchemaZod}
            dynamicValues={customValues}
            dynamicSections={customMetadataSections}
            isGmnAdmin={isGmnAdmin}
            onEditDynamicSchema={onEditDynamicSchema}
            groupKey={groupKey}
          />
        </div>
      </div>
    </div>
  );
}
